import {
	pgEnum,
	pgTable,
	pgSequence,
	boolean,
	integer,
	jsonb,
	numeric,
	text,
	uuid,
	date,
	timestamp,
	decimal,
	index,
	uniqueIndex,
	check,
	primaryKey
} from "drizzle-orm/pg-core";
import type { AnyPgColumn } from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

import { baseTableProps } from "../../baseTableProps";
import { cadParcels } from "../cad-parcels/cad-parcels.model";
import { addresses } from "../addresses/addresses.model";
import { companies } from "../companies/companies.model";
import { contacts } from "../contacts/contacts.model";
import type { SalesmateDealMeta } from "@fergmar/contracts/salesmate";

import { displayNameProps } from "../_shared/display-name";

export const investmentsTeamEnum = pgEnum("investments_team", ["short", "long"]);

export const investmentsPhaseEnum = pgEnum("investments_phase", [
	"lead",
	"entry",
	"management",
	"exiting",
	"exited",
	"abandoned",
	"terminated",
	"subdivided",
	"consolidated"
]);

export const investmentsAssetTypeEnum = pgEnum("investments_asset_type", [
	"land",
	"residential",
	"multifamily",
	"commercial"
]);

export const investmentsCapitalStructureEnum = pgEnum("investments_capital_structure", [
	"corporate",
	"syndication"
]);

export const investmentsManagementStatusEnum = pgEnum("investments_management_status", [
	"capex_planning",
	"capex_execution",
	"on_market",
	"lease_up",
	"stabilized"
]);

export const investmentsMarketingVisibilityEnum = pgEnum("investments_marketing_visibility", [
	"hidden",
	"listed",
	"featured"
]);

export type InvestmentCrmMetaData = {
	salesmate?: SalesmateDealMeta;
};

export const investmentsSequenceNumberSeq = pgSequence("investments_sequence_number_seq", {
	startWith: 6000,
	increment: 1,
	cache: 1
});

export const investments = pgTable(
	"investments",
	{
		...baseTableProps,
		sequenceNumber: integer("sequence_number")
			.notNull()
			.default(sql`nextval('investments_sequence_number_seq')`),
		team: investmentsTeamEnum("team").notNull(),
		entity: uuid("entity").references(() => companies.id, {
			name: "investments_entity_companies_id_fk"
		}),
		crmMetaData: jsonb("crm_meta_data").$type<InvestmentCrmMetaData>().default({}).notNull(),
		entryPrice: numeric("entry_price", { precision: 12, scale: 2, mode: "number" }),
		exitPrice: numeric("exit_price", { precision: 12, scale: 2, mode: "number" }),
		margin: numeric("margin", { precision: 12, scale: 2, mode: "number" }),
		costBasis: numeric("cost_basis", { precision: 12, scale: 2, mode: "number" }),
		phase: investmentsPhaseEnum("phase"),
		...displayNameProps,
		parentInvestmentId: uuid("parent_investment_id").references(
			(): AnyPgColumn => investments.id,
			{ name: "investments_parent_investment_id_investments_id_fk" }
		),
		tractIndex: integer("tract_index"),
		assetType: investmentsAssetTypeEnum("asset_type"),
		buildingUnitCount: integer("building_unit_count"),
		buildingSquareFeet: integer("building_square_feet"),
		lotSquareFeet: integer("lot_square_feet"),
		lotAcreage: numeric("lot_acreage", {
			precision: 10,
			scale: 2,
			mode: "number"
		}).generatedAlwaysAs(sql`lot_square_feet / 43560.0`),
		buildingYearBuilt: integer("building_year_built"),
		buildiumPropertyId: text("buildium_property_id"),
		propertyManager: uuid("property_manager").references(() => companies.id, {
			name: "investments_property_manager_companies_id_fk"
		}),
		managerPropertyName: text("manager_property_name"),
		capitalStructure: investmentsCapitalStructureEnum("capital_structure")
			.notNull()
			.default("corporate"),
		managementStatus: investmentsManagementStatusEnum("management_status"),
		legacyEntryDate: date("legacy_entry_date", { mode: "string" }),
		legacyExitDate: date("legacy_exit_date", { mode: "string" }),
		legacyEstimatedMarketValue: numeric("legacy_estimated_market_value", {
			precision: 12,
			scale: 2,
			mode: "number"
		}),
		legacyEstimatedMarketValueAsOf: timestamp("legacy_estimated_market_value_as_of", {
			withTimezone: true,
			mode: "string"
		}),
		legacyExitCapRate: decimal("legacy_exit_cap_rate", {
			precision: 7,
			scale: 4,
			mode: "number"
		}),
		legacyNetOperatingIncome: numeric("legacy_noi", {
			precision: 12,
			scale: 2,
			mode: "number"
		}),
		legacyNetOperatingIncomeAsOf: timestamp("legacy_noi_as_of", {
			withTimezone: true,
			mode: "string"
		}),
		legacyNetIncome: numeric("legacy_net_income", { precision: 12, scale: 2, mode: "number" }),
		legacyNetIncomeAsOf: timestamp("legacy_net_income_as_of", {
			withTimezone: true,
			mode: "string"
		}),
		marketingVisibility: investmentsMarketingVisibilityEnum("marketing_visibility")
			.notNull()
			.default("hidden"),
		monthlyUwTracking: boolean("monthly_uw_tracking").notNull().default(false)
	},
	(table) => [
		uniqueIndex("investments_parent_tract_uq")
			.on(table.parentInvestmentId, table.tractIndex)
			.where(sql`${table.parentInvestmentId} IS NOT NULL`),
		uniqueIndex("investments_sequence_tract_uq").on(
			table.sequenceNumber,
			sql`COALESCE(${table.tractIndex}, 0)`
		),
		check(
			"investments_management_status_phase_ck",
			sql`coalesce(${table.phase} = 'management', false) = (${table.managementStatus} IS NOT NULL)`
		),
		index("investments_entity_idx").on(table.entity),
		uniqueIndex("investments_property_manager_name_uq").on(
			table.propertyManager,
			table.managerPropertyName
		)
	]
);

export const investmentOwners = pgTable(
	"investment_owners",
	{
		...baseTableProps,
		investmentId: uuid("investment_id")
			.references(() => investments.id, {
				name: "investment_owners_investment_id_investments_id_fk",
				onDelete: "cascade"
			})
			.notNull(),
		companyId: uuid("company_id").references(() => companies.id, {
			name: "investment_owners_company_id_companies_id_fk",
			onDelete: "no action"
		}),
		contactId: uuid("contact_id").references(() => contacts.id, {
			name: "investment_owners_contact_id_contacts_id_fk",
			onDelete: "no action"
		}),
		ownershipPercentage: decimal("ownership_percentage", {
			precision: 11,
			scale: 8,
			mode: "number"
		}).notNull()
	},
	(table) => [
		check(
			"investment_owner_xor_check",
			sql`(${table.companyId} IS NULL) <> (${table.contactId} IS NULL)`
		),
		check(
			"investment_owner_ownership_percentage_check",
			sql`${table.ownershipPercentage} > 0 AND ${table.ownershipPercentage} <= 100`
		),
		uniqueIndex("investment_owner_company_uq")
			.on(table.investmentId, table.companyId)
			.where(sql`${table.companyId} IS NOT NULL`),
		uniqueIndex("investment_owner_contact_uq")
			.on(table.investmentId, table.contactId)
			.where(sql`${table.contactId} IS NOT NULL`),
		index("investment_owners_investment_id_idx").on(table.investmentId),
		index("investment_owners_company_id_idx").on(table.companyId),
		index("investment_owners_contact_id_idx").on(table.contactId)
	]
);

export const xInvestmentAddresses = pgTable(
	"x_investment_addresses",
	{
		...baseTableProps,
		id: uuid("id").notNull().defaultRandom(),
		investmentId: uuid("investment_id")
			.references(() => investments.id, {
				name: "x_investment_addresses_investment_id_investments_id_fk",
				onDelete: "cascade"
			})
			.notNull(),
		addressId: uuid("address_id")
			.references(() => addresses.id, {
				name: "x_investment_addresses_address_id_addresses_id_fk",
				onDelete: "cascade"
			})
			.notNull(),
		allocatedEntryPrice: numeric("allocated_entry_price", {
			precision: 12,
			scale: 2,
			mode: "number"
		}),
		allocatedExitPrice: numeric("allocated_exit_price", {
			precision: 12,
			scale: 2,
			mode: "number"
		})
	},
	(table) => [
		primaryKey({ name: "x_investment_properties_pkey", columns: [table.id] }),
		index("x_investment_addresses_investment_id_idx").on(table.investmentId),
		index("x_investment_addresses_address_id_idx").on(table.addressId)
	]
);

export const xInvestmentCadParcels = pgTable(
	"x_investment_cad_parcels",
	{
		...baseTableProps,
		investmentId: uuid("investment_id")
			.references(() => investments.id, {
				name: "x_investment_cad_parcels_investment_id_investments_id_fk",
				onDelete: "cascade"
			})
			.notNull(),
		cadParcelId: uuid("cad_parcel_id")
			.references(() => cadParcels.id, {
				name: "x_investment_cad_parcels_cad_parcel_id_cad_parcels_id_fk",
				onDelete: "cascade"
			})
			.notNull()
	},
	(table) => [
		index("x_investment_cad_parcels_investment_id_idx").on(table.investmentId),
		index("x_investment_cad_parcels_cad_parcel_id_idx").on(table.cadParcelId)
	]
);
